import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import {
  NeonButton,
  NeonCheckboxField,
  NeonCheckboxSingle,
  NeonFilePicker,
  NeonRadioField,
  NeonSystemMessage,
  NeonTextField,
} from '@ps-refarch-ux/neon';
import type { CheckboxOption, RadioOption } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  CONFIGURE_LABELS_LABEL,
  CONFIGURE_LABELS_DESCRIPTION,
  CONFIGURE_LABELS_HELPER,
  CONFIGURE_LABELS_MAX_LENGTH,
  CONFIGURE_LABELS_CLUSTER_DEFAULT,
  CONFIGURE_LABELS_PATHWAY_DEFAULT,
  TERM_SETTINGS_LABEL,
  TERM_SETTINGS_DESCRIPTION,
  TERM_SETTINGS_ENABLE_LABEL,
  TERM_SELECTION_LIST_LABEL,
  TERM_SELECTION_MAX_COUNT,
  TERM_SELECTION_MAX_LABEL,
  TERM_SELECTION_ADD_BUTTON,
  TERM_SELECTION_MAX_MESSAGE,
  TERM_NAMES_REQUIRED_INLINE_MESSAGE,
  SCHOOL_SELECTION_LABEL,
  SCHOOL_SELECTION_DESCRIPTION,
  SCHOOL_SELECTION_OPTIONS,
  SCHOOL_SELECTION_VALUES,
  SCHOOL_SELECTION_GRADES_LIST,
  SCHOOL_SELECTION_GRADES_LABEL,
  SCHOOL_SELECTION_GRADES_REQUIRED_INLINE_MESSAGE,
  ADDITIONAL_RESOURCES_LABEL,
  ADDITIONAL_RESOURCES_DESCRIPTION,
  ADDITIONAL_RESOURCES_SHOW_LABEL,
  ADDITIONAL_RESOURCES_LINKS_LABEL,
  ADDITIONAL_RESOURCES_MAX_LINKS,
  ADDITIONAL_RESOURCES_LINKS_MAX_LABEL,
  ADDITIONAL_RESOURCES_ADD_LINK,
  ADDITIONAL_RESOURCES_LINKS_MAX_MESSAGE,
  LINK_NAME_AND_URL_REQUIRED_INLINE_MESSAGE,
  ADDITIONAL_RESOURCES_CONTENT_REQUIRED_INLINE_MESSAGE,
  ADDITIONAL_RESOURCES_DOCUMENT_LABEL,
  ADDITIONAL_RESOURCES_DOC_NAME_LABEL,
  ADDITIONAL_RESOURCES_DOC_NAME_PLACEHOLDER,
  ADDITIONAL_RESOURCES_DOC_NAME_MAX,
  ADDITIONAL_RESOURCES_UPLOAD_LABEL,
  ADDITIONAL_RESOURCES_UPLOAD_HELPER,
  ADDITIONAL_RESOURCES_FILE_SIZE_LIMIT_KB,
  ADDITIONAL_RESOURCES_FILE_TOO_LARGE_MESSAGE,
  ADDITIONAL_RESOURCES_UPLOAD_BUTTON,
  ADDITIONAL_RESOURCES_REPLACE_BUTTON,
  areSchoolSelectionGradesValid,
} from '@/constants/configurations-constants';
import type {
  StudentExperienceFormState,
  AdditionalResourcesLink,
} from '@/constants/configurations-constants';
import {
  bodyTextStyle,
  tabContentBlockStyle,
  sectionBlockStyle,
  subsectionBlockStyle,
  sectionHeadingStyle,
  sectionDividerStyle,
} from './configurations.styles';
import { neonVar } from '@/utils/neon-utils';

const fontFamily = neonVar('font-family', 'Roboto, Arial, Helvetica, sans-serif');
const textColor = 'var(--Text-Color-Dark, #333333)';

const schoolSelectionOptions: RadioOption[] = SCHOOL_SELECTION_OPTIONS.map((o) => ({
  id: o.id,
  labelText: o.labelText,
  value: o.value,
}));

const schoolGradeOptions: CheckboxOption[] = SCHOOL_SELECTION_GRADES_LIST.map((grade) => ({
  id: `school-selection-grade-${grade}`,
  labelText: `${grade}`,
  name: `${grade}`,
}));

const labelGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  maxWidth: 700,
};

const termListBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '8px',
};

const termListHeadingStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: textColor,
  margin: 0,
};

const termListSubtextStyle: CSSProperties = {
  fontFamily,
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  color: textColor,
  margin: 0,
};

const termRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxWidth: 320,
};

const termRowInputLineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'end',
  gap: '16px',
};

const linkRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'end',
  gap: '16px',
  maxWidth: 640,
};

const schoolGradesBlockStyle: CSSProperties = {
  paddingLeft: '32px',
  paddingRight: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const docSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '4px',
};

const docNameWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxWidth: 320,
};

const uploadSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const fieldWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const fieldLabelStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: textColor,
  margin: 0,
};

function createDisplayFileList(fileName: string, file?: File): FileList | undefined {
  if (
    fileName.trim() === '' ||
    typeof DataTransfer === 'undefined' ||
    typeof File === 'undefined'
  ) {
    return undefined;
  }

  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file ?? new File([''], fileName));
  return dataTransfer.files;
}

type LabelTextFieldProps = {
  id: string;
  label: ReactNode;
  helperText: string;
  value: string;
  maxLength: number;
  placeholder?: string;
  onChange: (v: string) => void;
};

function LabelTextField({
  id,
  label,
  helperText,
  value,
  maxLength,
  placeholder,
  onChange,
}: LabelTextFieldProps): ReactElement {
  return (
    <div style={fieldWrapperStyle}>
      <label htmlFor={id} style={fieldLabelStyle}>
        {label}
      </label>
      <NeonTextField
        id={id}
        dataLabelText=""
        dataShowLabel={false}
        dataHelperText={helperText}
        dataMaxLength={maxLength}
        dataPlaceholderText={placeholder}
        dataShowCount={true}
        modelValue={value}
        modelValueChange={(v: string | undefined) => onChange(v ?? '')}
      />
    </div>
  );
}

type StudentExperienceTabProps = {
  formState: StudentExperienceFormState;
  onFormChange: (state: StudentExperienceFormState) => void;
  showTermSettings?: boolean;
  showSchoolSelection?: boolean;
  /** After a blocked Save, empty term rows are outlined until fixed */
  highlightEmptyTermNames?: boolean;
  /** After a blocked Save, link rows missing name or URL are outlined until fixed */
  highlightIncompleteLinks?: boolean;
  /** After a blocked Save when specific grades need at least one grade */
  highlightSchoolSelectionGrades?: boolean;
  /** After a blocked Save when Show to students is on but no link or document is present */
  highlightMissingAdditionalContent?: boolean;
};

export function StudentExperienceTab({
  formState,
  onFormChange,
  showTermSettings = true,
  showSchoolSelection = true,
  highlightEmptyTermNames = false,
  highlightIncompleteLinks = false,
  highlightSchoolSelectionGrades = false,
  highlightMissingAdditionalContent = false,
}: StudentExperienceTabProps): ReactElement {
  const [fileSizeError, setFileSizeError] = useState(false);
  const displayDocumentFiles = createDisplayFileList(
    formState.documentFileName,
    formState.documentFile
  );
  const displayTerms = formState.enableTermSetting
    ? formState.terms.length === 0
      ? ['']
      : formState.terms
    : [];

  const set = {
    clusterLabel: (v: string) => onFormChange({ ...formState, clusterLabel: v }),
    pathwayLabel: (v: string) => onFormChange({ ...formState, pathwayLabel: v }),
    enableTermSetting: () => {
      const newEnabled = !formState.enableTermSetting;
      const newTerms = newEnabled && formState.terms.length === 0 ? [''] : formState.terms;
      onFormChange({ ...formState, enableTermSetting: newEnabled, terms: newTerms });
    },
    termValue: (index: number, v: string) => {
      const next = [...displayTerms];
      next[index] = v;
      onFormChange({ ...formState, terms: next });
    },
    addTerm: () => {
      if (displayTerms.length < TERM_SELECTION_MAX_COUNT) {
        onFormChange({ ...formState, terms: [...displayTerms, ''] });
      }
    },
    deleteTerm: (index: number) => {
      const next = displayTerms.filter((_, i) => i !== index);
      onFormChange({ ...formState, terms: next });
    },
    schoolSelection: (v: string) =>
      onFormChange({
        ...formState,
        schoolSelection: v,
        schoolSelectionGrades:
          v === SCHOOL_SELECTION_VALUES.SpecificGrades ? formState.schoolSelectionGrades : [],
      }),
    showToStudents: () => onFormChange({ ...formState, showToStudents: !formState.showToStudents }),
    addLink: () => {
      if (formState.links.length < ADDITIONAL_RESOURCES_MAX_LINKS) {
        onFormChange({ ...formState, links: [...formState.links, { name: '', url: '' }] });
      }
    },
    updateLink: (index: number, field: keyof AdditionalResourcesLink, value: string) => {
      const next = formState.links.map((l, i) => (i === index ? { ...l, [field]: value } : l));
      onFormChange({ ...formState, links: next });
    },
    deleteLink: (index: number) => {
      onFormChange({ ...formState, links: formState.links.filter((_, i) => i !== index) });
    },
    documentName: (v: string) => onFormChange({ ...formState, documentName: v }),
    selectFile: (file: File) =>
      onFormChange({ ...formState, documentFileName: file.name, documentFile: file }),
    removeFile: () => onFormChange({ ...formState, documentFileName: '', documentFile: undefined }),
  };

  return (
    <div style={tabContentBlockStyle}>
      {/* Configure labels */}
      <section style={sectionBlockStyle} aria-labelledby="heading-configure-labels">
        <h2 id="heading-configure-labels" style={sectionHeadingStyle}>
          {CONFIGURE_LABELS_LABEL}
        </h2>
        <p style={bodyTextStyle}>{CONFIGURE_LABELS_DESCRIPTION}</p>
        <div style={labelGridStyle}>
          <LabelTextField
            id="cluster-label-field"
            label={
              <>
                Replace <strong>{CONFIGURE_LABELS_CLUSTER_DEFAULT}</strong> with
              </>
            }
            helperText={CONFIGURE_LABELS_HELPER}
            value={formState.clusterLabel}
            maxLength={CONFIGURE_LABELS_MAX_LENGTH}
            placeholder={`${CONFIGURE_LABELS_CLUSTER_DEFAULT} (default)`}
            onChange={set.clusterLabel}
          />
          <LabelTextField
            id="pathway-label-field"
            label={
              <>
                Replace <strong>{CONFIGURE_LABELS_PATHWAY_DEFAULT}</strong> with
              </>
            }
            helperText={CONFIGURE_LABELS_HELPER}
            value={formState.pathwayLabel}
            maxLength={CONFIGURE_LABELS_MAX_LENGTH}
            placeholder={`${CONFIGURE_LABELS_PATHWAY_DEFAULT} (default)`}
            onChange={set.pathwayLabel}
          />
        </div>
      </section>

      {showTermSettings && (
        <>
          <hr style={sectionDividerStyle} />

          {/* Term settings */}
          <section style={sectionBlockStyle} aria-labelledby="heading-term-settings">
            <h2 id="heading-term-settings" style={sectionHeadingStyle}>
              {TERM_SETTINGS_LABEL}
            </h2>
            <p style={bodyTextStyle}>{TERM_SETTINGS_DESCRIPTION}</p>
            <NeonCheckboxSingle
              id="enable-term-setting"
              dataLabelText={TERM_SETTINGS_ENABLE_LABEL}
              value={formState.enableTermSetting}
              onInput={set.enableTermSetting}
            />

            {formState.enableTermSetting && (
              <div style={termListBlockStyle}>
                <p style={termListHeadingStyle}>{TERM_SELECTION_LIST_LABEL}</p>
                <p style={termListSubtextStyle}>{TERM_SELECTION_MAX_LABEL}</p>

                {displayTerms.map((term, index) => (
                  <div key={index} style={termRowStyle}>
                    <div style={termRowInputLineStyle}>
                      <NeonTextField
                        id={`term-name-${index}`}
                        dataLabelText={`Term ${index + 1} name`}
                        dataShowLabel={true}
                        dataIsRequired={true}
                        dataSize={'x-large'}
                        customValidationFunction={(v: string | null | undefined) => ({
                          isValid: !(highlightEmptyTermNames && (v ?? '').trim() === ''),
                        })}
                        modelValue={term}
                        modelValueChange={(v: string | undefined) => set.termValue(index, v ?? '')}
                      />
                      {index > 0 && (
                        <NeonButton
                          id={`remove-term-${index}`}
                          dataType={NeonButtonTypes.Utility}
                          dataText=""
                          dataIcon="delete"
                          aria-label={`Remove term ${index + 1}`}
                          onClick={() => set.deleteTerm(index)}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {highlightEmptyTermNames && displayTerms.some((t) => t.trim() === '') && (
                  <NeonSystemMessage id="term-names-required-message">
                    <p data-slot="message-content">{TERM_NAMES_REQUIRED_INLINE_MESSAGE}</p>
                  </NeonSystemMessage>
                )}

                {displayTerms.length < TERM_SELECTION_MAX_COUNT ? (
                  <div>
                    <NeonButton
                      id="add-term-button"
                      dataType={NeonButtonTypes.Borderless}
                      dataText={TERM_SELECTION_ADD_BUTTON}
                      onClick={set.addTerm}
                    />
                  </div>
                ) : (
                  <NeonSystemMessage id="term-selection-max-message">
                    <p data-slot="message-content">{TERM_SELECTION_MAX_MESSAGE}</p>
                  </NeonSystemMessage>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {showSchoolSelection && (
        <>
          <hr style={sectionDividerStyle} />

          {/* School selection */}
          <section style={sectionBlockStyle} aria-labelledby="heading-school-selection">
            <h2 id="heading-school-selection" style={sectionHeadingStyle}>
              {SCHOOL_SELECTION_LABEL}
            </h2>
            <p style={bodyTextStyle}>{SCHOOL_SELECTION_DESCRIPTION}</p>
            <NeonRadioField
              id="school-selection-field"
              dataLabelText={SCHOOL_SELECTION_LABEL}
              dataShowLabel={false}
              modelValue={formState.schoolSelection}
              modelValueChange={(v) => v !== undefined && set.schoolSelection(v)}
              options={schoolSelectionOptions}
              dataControlLayout="column"
            />

            {formState.schoolSelection === SCHOOL_SELECTION_VALUES.SpecificGrades && (
              <div style={schoolGradesBlockStyle}>
                <NeonCheckboxField
                  id="school-selection-grades-field"
                  dataLabelText={SCHOOL_SELECTION_GRADES_LABEL}
                  dataIsRequired={true}
                  options={schoolGradeOptions}
                  dataControlLayout="row"
                  modelValue={formState.schoolSelectionGrades.map(String)}
                  modelValueChange={(selected) => {
                    const grades = (selected ?? []).map(Number).sort((a, b) => a - b);
                    onFormChange({ ...formState, schoolSelectionGrades: grades });
                  }}
                />
                {highlightSchoolSelectionGrades && !areSchoolSelectionGradesValid(formState) && (
                  <NeonSystemMessage id="school-selection-grades-required-message">
                    <p data-slot="message-content">
                      {SCHOOL_SELECTION_GRADES_REQUIRED_INLINE_MESSAGE}
                    </p>
                  </NeonSystemMessage>
                )}
              </div>
            )}
          </section>
        </>
      )}

      <hr style={sectionDividerStyle} />

      {/* Additional resources */}
      <section style={sectionBlockStyle} aria-labelledby="heading-additional-resources">
        <h2 id="heading-additional-resources" style={sectionHeadingStyle}>
          {ADDITIONAL_RESOURCES_LABEL}
        </h2>
        <p style={bodyTextStyle}>{ADDITIONAL_RESOURCES_DESCRIPTION}</p>
        <NeonCheckboxSingle
          id="show-to-students"
          dataLabelText={ADDITIONAL_RESOURCES_SHOW_LABEL}
          value={formState.showToStudents}
          onInput={set.showToStudents}
        />

        {highlightMissingAdditionalContent && (
          <NeonSystemMessage id="additional-resources-content-required-message">
            <p data-slot="message-content">
              {ADDITIONAL_RESOURCES_CONTENT_REQUIRED_INLINE_MESSAGE}
            </p>
          </NeonSystemMessage>
        )}

        {formState.showToStudents && (
          <>
            {/* Links */}
            <div style={subsectionBlockStyle}>
              <p style={termListHeadingStyle}>{ADDITIONAL_RESOURCES_LINKS_LABEL}</p>
              <p style={termListSubtextStyle}>{ADDITIONAL_RESOURCES_LINKS_MAX_LABEL}</p>

              {formState.links.map((link, index) => {
                return (
                  <div key={index} style={linkRowStyle}>
                    <NeonTextField
                      id={`link-name-${index}`}
                      dataLabelText={`Link name`}
                      dataShowLabel={true}
                      dataPlaceholderText="Name"
                      modelValue={link.name}
                      dataSize={'large'}
                      modelValueChange={(v: string | undefined) =>
                        set.updateLink(index, 'name', v ?? '')
                      }
                    />
                    <NeonTextField
                      id={`link-url-${index}`}
                      dataLabelText={`URL`}
                      dataShowLabel={true}
                      dataPlaceholderText={`e.g. www.Naviance.com`}
                      dataIsRequired={true}
                      dataSize={'x-large'}
                      customValidationFunction={(v: string | null | undefined) => ({
                        isValid: !(highlightIncompleteLinks && (v ?? '').trim() === ''),
                      })}
                      modelValue={link.url}
                      modelValueChange={(v: string | undefined) =>
                        set.updateLink(index, 'url', v ?? '')
                      }
                    />
                    <NeonButton
                      id={`delete-link-${index}`}
                      dataType={NeonButtonTypes.Utility}
                      dataText="Delete"
                      dataTooltipText="Delete"
                      dataIcon="delete"
                      aria-label={`Delete ${(link.name || `Link ${index + 1}`).trim()} link`}
                      onClick={() => set.deleteLink(index)}
                    />
                  </div>
                );
              })}

              {highlightIncompleteLinks && formState.links.some((l) => l.url.trim() === '') && (
                <NeonSystemMessage id="link-name-url-required-message">
                  <p data-slot="message-content">{LINK_NAME_AND_URL_REQUIRED_INLINE_MESSAGE}</p>
                </NeonSystemMessage>
              )}

              {formState.links.length < ADDITIONAL_RESOURCES_MAX_LINKS ? (
                <div>
                  <NeonButton
                    id="add-link-button"
                    dataType={NeonButtonTypes.Borderless}
                    dataText={ADDITIONAL_RESOURCES_ADD_LINK}
                    onClick={set.addLink}
                  />
                </div>
              ) : (
                <NeonSystemMessage id="additional-resources-max-links-message">
                  <p data-slot="message-content">{ADDITIONAL_RESOURCES_LINKS_MAX_MESSAGE}</p>
                </NeonSystemMessage>
              )}
            </div>

            {/* Document */}
            <div style={docSectionStyle}>
              <p style={termListHeadingStyle}>{ADDITIONAL_RESOURCES_DOCUMENT_LABEL}</p>

              <div style={docNameWrapperStyle}>
                <NeonTextField
                  id="doc-name-field"
                  dataLabelText={ADDITIONAL_RESOURCES_DOC_NAME_LABEL}
                  dataMaxLength={ADDITIONAL_RESOURCES_DOC_NAME_MAX}
                  dataPlaceholderText={ADDITIONAL_RESOURCES_DOC_NAME_PLACEHOLDER}
                  dataShowCount={true}
                  modelValue={formState.documentName}
                  modelValueChange={(v: string | undefined) => set.documentName(v ?? '')}
                />
              </div>

              <div style={uploadSectionStyle}>
                <NeonFilePicker
                  id="upload-file-picker"
                  dataLabelText={ADDITIONAL_RESOURCES_UPLOAD_LABEL}
                  dataHelperText={ADDITIONAL_RESOURCES_UPLOAD_HELPER}
                  dataShowSelectedFiles={true}
                  multiple={false}
                  dataButtonText={
                    formState.documentFileName
                      ? ADDITIONAL_RESOURCES_REPLACE_BUTTON
                      : ADDITIONAL_RESOURCES_UPLOAD_BUTTON
                  }
                  customValidationFunction={(_value, _condition) => {
                    return fileSizeError
                      ? {
                          isValid: false,
                          errorMessage: ADDITIONAL_RESOURCES_FILE_TOO_LARGE_MESSAGE,
                        }
                      : { isValid: true };
                  }}
                  modelValue={displayDocumentFiles}
                  modelValueChange={(files: FileList | undefined) => {
                    const file = files?.[0];
                    if (!file) {
                      setFileSizeError(false);
                      set.removeFile();
                      return;
                    }
                    if (file.size > ADDITIONAL_RESOURCES_FILE_SIZE_LIMIT_KB * 1024) {
                      setFileSizeError(true);
                      return;
                    }
                    setFileSizeError(false);
                    set.selectFile(file);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
